import React, { createContext, useContext, useState, useCallback } from 'react'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Download } from 'lucide-react'
import { Button } from './button'

interface ToastAction {
  label: string
  onClick: () => void
  icon?: React.ReactNode
}

interface Toast {
  id: string
  type: 'success' | 'error' | 'info' | 'warning'
  title: string
  description?: string
  duration?: number
  action?: ToastAction
}

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
  warning: (title: string, description?: string) => void
  persistent: (toast: Omit<Toast, 'id' | 'duration'>) => string
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast = { ...toast, id }

    setToasts(prev => [...prev, newToast])

    // Auto-remove after duration (0 or undefined = persistent)
    const duration = toast.duration ?? 5000
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [removeToast])

  const success = useCallback((title: string, description?: string) => {
    addToast({ type: 'success', title, description })
  }, [addToast])

  const error = useCallback((title: string, description?: string) => {
    addToast({ type: 'error', title, description, duration: 7000 })
  }, [addToast])

  const info = useCallback((title: string, description?: string) => {
    addToast({ type: 'info', title, description })
  }, [addToast])

  const warning = useCallback((title: string, description?: string) => {
    addToast({ type: 'warning', title, description })
  }, [addToast])

  // Persistent toast that stays until dismissed, returns ID for programmatic removal
  const persistent = useCallback((toast: Omit<Toast, 'id' | 'duration'>): string => {
    return addToast({ ...toast, duration: 0 })
  }, [addToast])

  return (
    <ToastContext.Provider value={{
      toasts,
      addToast,
      removeToast,
      success,
      error,
      info,
      warning,
      persistent
    }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

function ToastContainer() {
  const { toasts, removeToast } = useToast()

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 left-4 z-50 space-y-2">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'error': return <AlertCircle className="h-4 w-4 text-red-600" />
      case 'warning': return <AlertTriangle className="h-4 w-4 text-orange-600" />
      case 'info': return <Info className="h-4 w-4 text-blue-600" />
    }
  }

  const getBorderColor = () => {
    switch (toast.type) {
      case 'success': return 'border-green-200'
      case 'error': return 'border-red-200'
      case 'warning': return 'border-orange-200'
      case 'info': return 'border-blue-200'
    }
  }

  return (
    <div
      className={`bg-white/90 backdrop-blur-sm border rounded-none shadow-lg p-4 max-w-md w-full ${getBorderColor()} 
        animate-in slide-in-from-bottom-full duration-300`}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{toast.title}</div>
          {toast.description && (
            <div className="text-xs text-muted-foreground mt-1">{toast.description}</div>
          )}
          {toast.action && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 h-7 text-xs gap-1"
              onClick={toast.action.onClick}
            >
              {toast.action.icon}
              {toast.action.label}
            </Button>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 hover:bg-muted"
          onClick={() => onRemove(toast.id)}
          aria-label="Dismiss notification"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
